import React from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { faCircleCheck, faClose, faPlus, faSackDollar } from "@fortawesome/free-solid-svg-icons"
import { ButtonIcon } from "../../components/Button/Button"
import List from "../../components/List/List"
import { AdminListCard, AdminListMetricItem } from "./components/AdminListCard"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { convertToLocalMoney, formatDateToString } from "../../Objects/Generics/Helps"
import { EResellerCommissionEntryStatus } from "../../Enum/EResellerCommissionEntryStatus"
import type { ResellerCommissionEntry } from "../../hooks/Reseller/useGetResellerCommissions"
import GetSpace from "../../utils/space"

interface ResellerCommissionStatementPopupProps {
  entries: ResellerCommissionEntry[]
  onMarkPaid: (entryId: number) => void
  onCreatePayment: () => void
  onClose: () => void
}

export const ResellerCommissionStatementPopup: React.FC<ResellerCommissionStatementPopupProps> = ({
  entries,
  onMarkPaid,
  onCreatePayment,
  onClose,
}) => {
  const { t } = useTranslation("admin")

  const hasPendingCommission = entries.some((entry) => entry.status !== EResellerCommissionEntryStatus.PaidOut)

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={`${defaultStyle.alert} max-h-[85vh] overflow-y-auto`}>
            <TitlePageResponsiveIconSmall
              title={t("reseller_commissions_title")}
              icon={faSackDollar}
              iconColor={THEME.colors.iconBGColor}
              children={
                <>
                  {hasPendingCommission && (
                    <>
                      <ButtonIcon
                        typeButton="button"
                        icon={faPlus}
                        bgColor={THEME.colors.buttonBGSaveColor}
                        onClick={onCreatePayment}
                        descriptionKey="interactionDescriptions:pages.adminDashboard.resellerCommissionStatementPopup.button.createPayment"
                      />

                      <GetSpace left={5} />
                    </>
                  )}

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    descriptionKey="interactionDescriptions:pages.adminDashboard.resellerCommissionStatementPopup.button.onClose"
                  />
                </>
              }
            />

            <div className="mt-2 p-2">
              <List
                showFilterStatus={false}
                messageCount={t("reseller_commissions_count")}
                messageEmpty={t("reseller_commissions_empty")}
                nameFindSearch={t("reseller_commissions_search")}
                handleOnClick={() => {}}
                data={entries.map((entry, index) => {
                  const isPaid = entry.status === EResellerCommissionEntryStatus.PaidOut

                  return {
                    id: entry.id,
                    index,
                    title: entry.companyName || "—",
                    linkImage: "",
                    isDisable: isPaid,
                    dataFilter: [entry.companyName],
                    children: (
                      <AdminListCard
                        title={entry.companyName || "—"}
                        subtitle={convertToLocalMoney(entry.commissionAmount)}
                        badge={isPaid ? t("reseller_status_paid") : t("reseller_status_accrued")}
                        badgeClass={isPaid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}
                        cardClass={
                          isPaid
                            ? "rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-3"
                            : "rounded-xl border-2 border-amber-200 bg-amber-50/40 p-3"
                        }
                      >
                        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                          <AdminListMetricItem
                            label={t("reseller_column_payment")}
                            value={convertToLocalMoney(entry.grossPaymentAmount)}
                          />

                          <AdminListMetricItem
                            label={t("reseller_column_commission_amount")}
                            value={convertToLocalMoney(entry.commissionAmount)}
                            accent="text-orange-700"
                          />

                          <AdminListMetricItem
                            label={t("reseller_column_date")}
                            value={formatDateToString(entry.accruedAtUtc)}
                          />
                        </div>

                        {!isPaid && (
                          <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
                            <button
                              type="button"
                              className="inline-flex min-h-[34px] items-center justify-center gap-1 rounded-lg border border-emerald-400 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 transition-colors hover:bg-emerald-100"
                              onClick={(event) => {
                                event.stopPropagation()
                                onMarkPaid(entry.id)
                              }}
                              data-interaction-description-key="interactionDescriptions:pages.adminDashboard.resellerCommissionStatementPopup.action.markPaid"
                            >
                              <FontAwesomeIcon icon={faCircleCheck} />
                              <span>{t("reseller_mark_paid_action")}</span>
                            </button>
                          </div>
                        )}
                      </AdminListCard>
                    ),
                  }
                })}
              />
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
